/**
 * AmbientAudioToggle — tiny speaker button for the Header.
 *
 * Doesn't own the audio engine — just emits CustomEvents that the
 * GlobalAmbientAudio component listens for. This keeps the audio state
 * truly singleton and the toggle trivially swappable / testable.
 *
 * Visual states:
 *   • enabled + running   → gold speaker, subtle glow
 *   • enabled + suspended → gold speaker, pulsing idle hint
 *   • disabled            → muted speaker with cross
 */
import { useEffect, useState } from "react";
import { useLocale } from "@/lib/locale";

export default function AmbientAudioToggle({ className = "" }) {
    const { t } = useLocale();
    const [enabled, setEnabled] = useState(true);
    const [running, setRunning] = useState(false);

    useEffect(() => {
        const onStarted = () => setRunning(true);
        const onStopped = () => setRunning(false);
        const onStatus = (e) => {
            setEnabled(!!e.detail?.enabled);
            setRunning(!!e.detail?.running);
        };
        window.addEventListener("ambientaudio:started", onStarted);
        window.addEventListener("ambientaudio:stopped", onStopped);
        window.addEventListener("ambientaudio:status", onStatus);
        // Ask for current state (handled with a 100ms debounce in engine).
        window.dispatchEvent(new CustomEvent("ambientaudio:query"));
        return () => {
            window.removeEventListener("ambientaudio:started", onStarted);
            window.removeEventListener("ambientaudio:stopped", onStopped);
            window.removeEventListener("ambientaudio:status", onStatus);
        };
    }, []);

    const click = () => {
        window.dispatchEvent(new CustomEvent("ambientaudio:toggle"));
        // Optimistic flip so the icon feels snappy
        setEnabled((prev) => !prev);
    };

    // Labels — reuse existing moment.* keys for IT/EN/ES/PT/FR coverage
    const label = enabled ? t("moment.audio_off") : t("moment.audio_on");
    const idleHint = enabled && !running;

    return (
        <button
            type="button"
            onClick={click}
            title={label}
            aria-label={label}
            aria-pressed={enabled}
            data-testid="global-audio-toggle"
            data-state={enabled ? (running ? "running" : "idle") : "off"}
            className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition-all duration-500 ${
                enabled
                    ? `${running ? "border-ink-gold/70 text-ink-goldHi bg-ink-gold/10" : "border-ink-gold/50 text-ink-goldHi"}${idleHint ? " animate-pulse" : ""}`
                    : "border-ink-border text-ink-textLow hover:text-ink-textMid"
            } ${className}`}
        >
            <svg
                viewBox="0 0 24 24" width="14" height="14" fill="none"
                stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
                strokeLinejoin="round" aria-hidden="true"
                className={enabled ? "opacity-95" : "opacity-70"}
            >
                {enabled ? (
                    <>
                        <path d="M11 5 6 9H2v6h4l5 4V5z" />
                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    </>
                ) : (
                    <>
                        <path d="M11 5 6 9H2v6h4l5 4V5z" />
                        <line x1="22" y1="9" x2="16" y2="15" />
                        <line x1="16" y1="9" x2="22" y2="15" />
                    </>
                )}
            </svg>
        </button>
    );
}
